// Enterprise Warranty Utilities
import type { Warranty, WarrantyStats } from '../types/warranty';

// Define the types locally if they're missing from main types
type EntryMethodDisplay = {
  icon: string;
  label: string;
};

/**
 * Get category icon and label for warranty category
 */
export const getCategoryIcon = (category: string): string => {
  const icons: Record<string, string> = {
    electronics: 'smart-solution',
    appliances: 'appliances', 
    automotive: 'manage',
    home: 'warranty-shield',
    other: 'storage'
  };
  return icons[category] || icons.other;
};

/**
 * Get entry method display information
 */
export const getEntryMethodDisplay = (method: string): EntryMethodDisplay => {
  const methods: Record<string, EntryMethodDisplay> = {
    manual: { icon: 'manage', label: 'Manual Entry' },
    'document-extraction': { icon: 'ocr-camera', label: 'Document Scan' },
    'auto-import': { icon: 'smart-analytics', label: 'Auto Import' }
  };
  return methods[method] || methods.manual;
};

/**
 * Check if warranty is expired
 */
export const isExpired = (expiryDate: string): boolean => {
  return new Date(expiryDate) < new Date();
};

/**
 * Check if warranty is expiring soon (within 30 days)
 */
export const isExpiringSoon = (expiryDate: string, daysThreshold: number = 30): boolean => {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const timeDiff = expiry.getTime() - today.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return daysDiff <= daysThreshold && daysDiff > 0;
};

/**
 * Get warranty status based on expiry date
 */
export const getWarrantyStatus = (warranty: Warranty): 'active' | 'expiring' | 'expired' => {
  if (isExpired(warranty.warrantyExpiryDate)) return 'expired';
  if (isExpiringSoon(warranty.warrantyExpiryDate)) return 'expiring';
  return 'active';
};

/**
 * Calculate warranty statistics
 */
export const calculateWarrantyStats = (warranties: Warranty[]): WarrantyStats => {
  return {
    total: warranties.length,
    active: warranties.filter(w => getWarrantyStatus(w) === 'active').length,
    expiring: warranties.filter(w => getWarrantyStatus(w) === 'expiring').length,
    expired: warranties.filter(w => getWarrantyStatus(w) === 'expired').length
  };
};

/**
 * Filter warranties based on search and filter criteria
 */
export const filterWarranties = (
  warranties: Warranty[],
  filters: {
    searchTerm: string;
    statusFilter: string;
    categoryFilter: string;
    providerFilter: string;
  }
): Warranty[] => {
  return warranties.filter(warranty => {
    const status = getWarrantyStatus(warranty);
    
    const matchesStatus = filters.statusFilter === 'all' || status === filters.statusFilter;
    const matchesSearch = 
      warranty.productName.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      warranty.brand.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      warranty.modelNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      warranty.category.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    const matchesCategory = filters.categoryFilter === 'all' || warranty.category === filters.categoryFilter;
    const matchesProvider = filters.providerFilter === 'all' || warranty.warrantyProvider === filters.providerFilter;
    
    return matchesStatus && matchesSearch && matchesCategory && matchesProvider;
  });
};

/**
 * Sort warranties by expiry date (soonest first)
 */
export const sortWarrantiesByExpiry = (warranties: Warranty[]): Warranty[] => {
  return [...warranties].sort((a, b) => 
    new Date(a.warrantyExpiryDate).getTime() - new Date(b.warrantyExpiryDate).getTime()
  );
};

/**
 * Get unique categories from warranties
 */
export const getUniqueCategories = (warranties: Warranty[]): string[] => {
  return [...new Set(warranties.map(w => w.category).filter(Boolean))];
};

/**
 * Get unique providers from warranties
 */
export const getUniqueProviders = (warranties: Warranty[]): string[] => {
  return [...new Set(warranties.map(w => w.warrantyProvider).filter(Boolean))];
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString();
};

/**
 * Get days until expiry
 */
export const getDaysUntilExpiry = (expiryDate: string): number => {
  const expiry = new Date(expiryDate);
  const today = new Date();
  const timeDiff = expiry.getTime() - today.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};
